"use client";

/**
 * P2-06f · Promotion countdown (client).
 *
 * Ticks once a second to the target. Renders nothing until mounted (avoids a
 * server/client time mismatch), when `prefers-reduced-motion` is set (a ticking
 * timer is motion), or once the target has passed. Decorative — `aria-hidden`,
 * so the per-second updates don't spam assistive tech; the promo copy carries
 * the real message.
 */
import { useEffect, useState } from "react";

import {
  countdownParts,
  type CountdownParts,
} from "~/server/content/blocks/promotion";

const UNITS: Array<{ key: keyof CountdownParts; label: string }> = [
  { key: "days", label: "d" },
  { key: "hours", label: "h" },
  { key: "minutes", label: "m" },
  { key: "seconds", label: "s" },
];

export function PromoCountdown({ target }: { target: string }) {
  const [parts, setParts] = useState<CountdownParts | null>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const targetMs = new Date(target).getTime();
    if (Number.isNaN(targetMs)) return;

    const tick = () => {
      const next = countdownParts(targetMs, Date.now());
      setParts(next);
      setShow(next !== null);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);

  if (!show || !parts) return null;

  return (
    <div
      aria-hidden
      className="flex items-center gap-2 font-mono text-sm tabular-nums"
    >
      {UNITS.map(({ key, label }) => (
        <span
          key={key}
          className="inline-flex min-w-[3ch] flex-col items-center rounded-[var(--radius-sm)] bg-black/20 px-2 py-1 leading-tight"
        >
          <span className="text-base font-semibold">
            {String(parts[key]).padStart(2, "0")}
          </span>
          <span className="text-[10px] uppercase opacity-80">{label}</span>
        </span>
      ))}
    </div>
  );
}
